import { UserType } from '@/types/user'
import { RoleEnum } from '@/enums'

/**
 * Check if a user has a specific permission action
 * @param user - The user object from the store
 * @param permissionName - The permission name (e.g., 'CATEGORIES', 'BLOG_POSTS')
 * @param actionName - The action name (e.g., 'LIST', 'CREATE', 'DELETE')
 * @returns true if user has the permission, false otherwise
 */
export const hasPermission = (user: UserType | null, permissionName: string, actionName: string): boolean => {
  if (!user) {
    return false
  }

  // Root role bypasses all permission checks
  if (user.roles?.includes(RoleEnum.ROOT)) {
    return true
  }

  if (!user.permissions) {
    return false
  }

  // Check user permissions directly
  const permission = user.permissions.find(p => p.name === permissionName)
  if (!permission) {
    return false
  }

  // Check if the action exists in the permission
  return permission.actions.some(a => a.name === actionName)
}

/**
 * Check if a user has any of the specified permissions
 * @param user - The user object from the store
 * @param permissions - Array of permission checks [permissionName, actionName]
 * @returns true if user has at least one permission, false otherwise
 */
export const hasAnyPermission = (user: UserType | null, permissions: Array<[string, string]>): boolean =>
  permissions.some(([permissionName, actionName]) => hasPermission(user, permissionName, actionName))

/**
 * Get all user permissions as a flat array of "PERMISSION_ACTION" strings
 * @param user - The user object from the store
 * @returns Array of permission strings like ['CATEGORIES_LIST', 'BLOG_POSTS_CREATE']
 */
export const getUserPermissions = (user: UserType | null): string[] => {
  if (!user || !user.permissions) {
    return []
  }

  const permissions = new Set<string>()

  for (const permission of user.permissions) {
    for (const action of permission.actions) {
      permissions.add(`${permission.name}_${action.name}`)
    }
  }

  return Array.from(permissions)
}
